/**
 * Recommend up to N tags for the deployment-scope picker on the
 * App Config + PPPC wizards. Used to seed the external-target empty state.
 *
 * Heuristic, in order of preference:
 *   1. Tags whose name (case-insensitive) contains the app name.
 *   2. Tags whose name matches "All Macs" / "All Devices" (broad fleet).
 *   3. Dynamic tags (likely to track OS/model and need least manual upkeep).
 *   4. Any remaining tags, in the order returned by the API.
 *
 * Result is de-duplicated and capped at `limit`.
 */
export interface RecommendableTag {
  id: string;
  name: string;
  type: string;
  color: string;
  icon?: string;
}

export function recommendTagsForApp<T extends RecommendableTag>(
  available: T[],
  appName: string | undefined,
  limit = 3,
): T[] {
  if (!available || available.length === 0) return [];
  const name = (appName ?? "").trim().toLowerCase();

  const byAppName = name
    ? available.filter((t) => t.name.toLowerCase().includes(name))
    : [];
  const broad = available.filter((t) =>
    /^all\s+(macs|devices)$/i.test(t.name.trim()),
  );
  const dynamic = available.filter((t) => t.type?.toLowerCase() === "dynamic");

  const seen = new Set<string>();
  const out: T[] = [];
  for (const bucket of [byAppName, broad, dynamic, available]) {
    for (const tag of bucket) {
      if (out.length >= limit) return out;
      if (seen.has(tag.id)) continue;
      seen.add(tag.id);
      out.push(tag);
    }
  }
  return out;
}
